import { useCallback, useEffect, useRef, useState } from 'react';
import { scenarios } from '../data/beheer/system';
import { db } from '../utils/db';
import type { ScenarioResult } from '../types/beheer';

export interface ScenarioRunState {
  running: boolean;
  step: number;
  result: ScenarioResult;
  lastRun: string;
  duration: string;
  note: string | null;
}

const STEP_MS = 650;

function initial(): Record<string, ScenarioRunState> {
  return Object.fromEntries(
    scenarios.map((s) => [
      s.id,
      { running: false, step: 0, result: s.lastResult, lastRun: s.lastRun, duration: s.lastDuration, note: null },
    ]),
  );
}

/** Simulates scenario test runs step by step; each finished run writes a logbook row. */
export function useScenarioRunner() {
  const [runs, setRuns] = useState<Record<string, ScenarioRunState>>(initial);
  const timers = useRef<number[]>([]);

  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

  const run = useCallback((id: string) => {
    const s = scenarios.find((x) => x.id === id);
    if (!s) return;
    setRuns((r) => (r[id].running ? r : { ...r, [id]: { ...r[id], running: true, step: 0, note: null } }));
    s.steps.forEach((_, i) => {
      timers.current.push(
        window.setTimeout(() => setRuns((r) => ({ ...r, [id]: { ...r[id], step: i } })), i * STEP_MS),
      );
    });
    timers.current.push(
      window.setTimeout(() => {
        const seconds = ((s.steps.length * STEP_MS) / 1000).toFixed(1).replace('.', ',');
        setRuns((r) => ({
          ...r,
          [id]: { running: false, step: s.steps.length, result: s.runResult, lastRun: `vandaag ${db.getState().meta.time}`, duration: `${seconds} s`, note: s.runNote },
        }));
        db.log({
          agent: 'systeem',
          summary: `Scenariotest "${s.name}" ${s.runResult}`,
          level: 'zelf',
          where: 'lokaal',
          outcome: s.runResult === 'mislukt' ? 'gemeld' : 'uitgevoerd',
        });
      }, s.steps.length * STEP_MS),
    );
  }, []);

  const runAll = useCallback(() => {
    scenarios.forEach((s, i) => {
      timers.current.push(window.setTimeout(() => run(s.id), i * 180));
    });
  }, [run]);

  return { runs, run, runAll };
}
